import React from 'react';

import {Button, CheckIcon, ModalPopup} from '@kit';

import {useCharacter} from '../../../character-context';
import {resourceKey} from '../../../model/procedures';
import type {SpellSlot} from '../../../model/spell-slots';
import {useFeatureAction} from '../use-feature-action';
import type {ProcedureModalProps} from './procedure-modal-props';

import css from '../../sheet.module.css';

/**
 * Источник магии и Мистическая сосредоточенность: ячейку можно потратить и вернуть очки по её кругу,
 * а очки — превратить в ячейку по таблице цен. Созданные сверх максимума ячейки исчезают после Долгого отдыха.
 */
export const SlotConversionModal: React.FC<ProcedureModalProps<'slotConversion'>> = ({action, procedure, onClose}) => {
	const {character} = useCharacter();
	const {record} = useFeatureAction(action);
	const key = resourceKey(procedure.resource);
	const pool = character.limitedUse(key);
	const points = pool ? pool.max - pool.spent : 0;
	const slots = character.spellSlots.filter((slot) => !slot.pact);

	const slotToPoints = (slot: SpellSlot) => {
		const gained = Math.min(slot.level, pool?.spent || 0);
		record(`ячейка ${slot.level} круга → ${pool?.title}: +${gained}`, (current) => ({
			spentSpellSlots: {...current.spentSpellSlots, [slot.key]: (current.spentSpellSlots[slot.key] || 0) + 1},
			spentUses: {...current.spentUses, [key]: Math.max(0, (current.spentUses[key] || 0) - gained)},
		}));
	};

	const pointsToSlot = (slot: SpellSlot, cost: number) => {
		record(`${pool?.title}: −${cost} → ячейка ${slot.level} круга`, (current) => ({
			spentUses: {...current.spentUses, [key]: (current.spentUses[key] || 0) + cost},
			spentSpellSlots: {...current.spentSpellSlots, [slot.key]: (current.spentSpellSlots[slot.key] || 0) - 1},
		}));
	};

	const buyable = slots.filter((slot) => {
		const cost = procedure.costs[slot.level];
		return cost != null && procedure.ownerLevel >= (procedure.minLevels[slot.level] ?? 0);
	});

	return (
		<ModalPopup open onClose={onClose} header={action.title} maxWidth={560}>
			<p>
				{pool?.title}: <b>{points}</b> из {pool?.max ?? 0}.
			</p>

			<h3 className={css.groupTitle}>Ячейка → {pool?.title}</h3>
			<div className={css.formRows}>
				{slots.map((slot) => (
					<div key={slot.key} className={css.formRow}>
						<span>
							{slot.level} круг <span className={css.muted}>· свободно {slot.max - slot.spent}</span>
						</span>
						<Button size="small" disabled={slot.spent >= slot.max} onClick={() => slotToPoints(slot)}>
							Потратить → +{slot.level}
						</Button>
					</div>
				))}
			</div>

			<h3 className={css.groupTitle}>
				{pool?.title} → {procedure.restoreOnly ? 'восстановить ячейку' : 'создать ячейку'}
			</h3>
			<div className={css.formRows}>
				{buyable.map((slot) => {
					const cost = procedure.costs[slot.level];
					const isAllowed = points >= cost && (!procedure.restoreOnly || slot.spent > 0);
					return (
						<div key={slot.key} className={css.formRow}>
							<span>
								{slot.level} круг <span className={css.muted}>· цена {cost}</span>
							</span>
							<Button size="small" disabled={!isAllowed} onClick={() => pointsToSlot(slot, cost)}>
								{procedure.restoreOnly ? 'Восстановить' : 'Создать'}
							</Button>
						</div>
					);
				})}
			</div>

			<div className={css.modalButtons}>
				<Button appearance="primary" icon={<CheckIcon />} onClick={onClose}>
					Готово
				</Button>
			</div>
		</ModalPopup>
	);
};
